'use client'

import { cx } from '@/app/lib/utils'
import { IconChevLeft, IconChevRight } from '@/app/components/atoms/Icons'
import styles from './ArrowBtn.module.scss'
import type { ArrowBtnProps } from './types'

/** Etiquetas accesibles por defecto según la dirección. */
const DEFAULT_LABEL = {
  left: 'Anterior',
  right: 'Siguiente',
} as const

/**
 * ArrowBtn — botón de navegación (anterior / siguiente) para carruseles y raíles.
 *
 * Client Component: reenvía `onClick` a un `<button>` real, por lo que necesita
 * adjuntar un manejador de eventos en el cliente. El estado de hover se resuelve
 * en CSS (no usa hooks), así que el único motivo del `'use client'` es el evento.
 *
 * Accesibilidad: el botón solo contiene un icono, por lo que siempre lleva
 * `aria-label` ('Anterior' / 'Siguiente' por defecto). El icono es decorativo.
 */
export function ArrowBtn({
  dir = 'right',
  onClick,
  dark = false,
  overlay = false,
  disabled = false,
  width,
  height,
  ariaLabel,
  className,
}: ArrowBtnProps) {
  const Icon = dir === 'left' ? IconChevLeft : IconChevRight

  return (
    <button
      type="button"
      className={cx(
        styles.btn,
        // `overlay` prevalece: su fondo neón sólido ya cubre
        // el caso de fondo oscuro, y combinar ambas dejaría el borde de `dark`.
        overlay ? styles['btn--overlay'] : dark && styles['btn--dark'],
        className
      )}
      // Dimensión dinámica desde props: solo cuando el consumidor necesita
      // igualar el hueco de otro elemento rectangular adyacente (p. ej. los
      // días de 44×56 de una tira de fechas) — el resto de usos no pasa
      // `width`/`height` y conserva el tamaño fijo del token (48px).
      style={width !== undefined || height !== undefined ? { width, height } : undefined}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel ?? DEFAULT_LABEL[dir]}
    >
      <Icon size={20} />
    </button>
  )
}
